import { FormControlLabel } from '@mui/material';
import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { CheckboxField } from '@procureai/ui-form-mui';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { SvbrgCatalogueItem } from '@/generated/gql/graphql';
import {
  IntakeExtendedLineItemsCheckStepFormData,
  IntakeSelectableExtendedLineItemData,
} from '@/legacy/routes/IntakeDetail/steps/IntakeExtendedLineItemsCheckStep/IntakeExtendedLineItemsCheckStep.schema';
import { I18nNamespace } from '~/common/types';
import { useConfig } from '~/components/ConfigProvider';

export interface SelectCellProps<R extends GridValidRowModel = any> {
  row: R;
  value?: Union.Nullable<string>;
  prefix?: Union.Nullable<string>;
}

export const SelectCell = ({ row, value, prefix }: SelectCellProps<SvbrgCatalogueItem>) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeExtendedLineItemsCheckStep' });
  const { fallbackCatalogueSupplierID } = useConfig();
  const checkId = value as string;
  const item: SvbrgCatalogueItem = row;
  const { control, watch } = useFormContext<IntakeExtendedLineItemsCheckStepFormData>();

  // Watch the selected item id for this checkId
  const selectedItem = watch(`${prefix ?? ''}.${checkId ?? ''}` as `items.${string}`);

  // Map SvbrgContractItem to expected form shape
  // Position will be assigned by the check
  const mapToFormShape = (): IntakeSelectableExtendedLineItemData => ({
    title: item.name,
    description: item.description ?? undefined,
    pricePerUnit: item.price?.toString() ?? undefined,
    id: item.id,
    currency: item.currency ?? 'EUR',
    supplierId: fallbackCatalogueSupplierID,
  });

  const isChecked = typeof selectedItem !== 'string' && selectedItem?.id === item.id;

  return (
    <Controller
      name={`${prefix ?? ''}.${checkId ?? ''}` as `items.${string}`}
      control={control}
      render={({ field }) => (
        <FormControlLabel
          control={
            <CheckboxField
              value={isChecked}
              onChange={(checked: boolean | null) => {
                if (checked) {
                  field.onChange(mapToFormShape());
                } else {
                  field.onChange(undefined);
                }
              }}
            />
          }
          label={t('button.select')}
        />
      )}
    />
  );
};
